<template>
  <div ref="wrapper" :class="{'dh-psy-voice': true, 'dh-psy-voice--dark': extraDark, 'dh-psy-voice--not-intro': !isIntro, 'dh-psy-voice--in-view': activeSection !== -1 }" v-if="sound && sound.captions">
    <div v-if="imageId" class="dh-psy-voice__bg">
      <ImageSrcSet :imageId="imageId" />
    </div>
    <div class="dh-psy-voice__fg">
      <div class="dh-psy-voice__sticky">
        <div class="dh-psy-voice__control">
          <button @click="handleTogglePlay" class="nrk-button">
            <svg v-if="status === 'PLAYING'" style="width:1.5em;height:1.5em" focusable="false" aria-hidden="true">
              <use xlink:href="#nrk-media-pause" /></svg>
            <svg v-else style="width:1.5em;height:1.5em" focusable="false" aria-hidden="true">
              <use xlink:href="#nrk-media-play" /></svg>
            <span v-if="status === 'PLAYING'" class="nrk-sr">Pause</span>
            <span v-else class="nrk-sr">Spill av</span>
          </button>
          <div ref="wave" class="dh-psy-voice__wave" aria-hidden></div>
          <div class="dh-psy-voice__duration" aria-hidden>{{ formattedDuration }}</div>
        </div>
        <div :aria-hidden="!showInsentive" :class="{'dh-psy-voice__insentive': true, 'dh-psy-voice__insentive--hidden': !showInsentive }">
          <div>
            <button class="nrk-unset" @click="handleTogglePlay" v-if="callToAction" v-html="callToAction" />
            <button class="nrk-unset" @click="handleTogglePlay" v-else>Hør lydenklippet</button>
          </div>
        </div>
        <div :aria-hidden="!showNext" :class="{'dh-psy-voice__scroll-next': true, 'dh-psy-voice__scroll-next--hidden': showNext === false || activeSection === -1 }">
          <div>
            <svg style="width:1.5em;height:1.5em" focusable="false" aria-hidden="true">
              <use xlink:href="#nrk-chevron-down" /></svg>
          </div>
        </div>
      </div>
      <Scroller @change="handleActiveChange" :root-margin="rootMargin">
        <div ref="foreground" slot="foreground" :class="{'dh-psy-voice__captions': true, 'dh-psy-voice__captions--activated': activated }">
          <div :class="{'dh-psy-voice__captions-section': true, 'dh-psy-voice__captions-section--active': sectionIdx === activeSection, 'dh-psy-voice__captions-section--passed': sectionIdx < activeSection }" v-for="(section, sectionIdx) in sectionCaptions" :key="sectionIdx">
            <div>
              <div v-for="(caption, captionIdx) in section" :key="caption.idx" :class="{'dh-psy-voice__caption-item': true, 'dh-psy-voice__caption-item--passed': caption.idx < currentIndex, 'dh-psy-voice__caption-item--active': caption.idx === currentIndex}">

                <span v-if="section.length === 1" v-html="`&laquo;${caption.text}&raquo;`"></span>
                <span v-else-if="captionIdx === 0" v-html="`&laquo;${caption.text}`"></span>
                <span v-else-if="captionIdx === section.length - 1" v-html="`${caption.text}&raquo;`"></span>
                <span v-else v-html="caption.text"></span>
              </div>
            </div>
          </div>
        </div>
      </Scroller>
    </div>
  </div>
</template>

<script>
import { getSound } from '../../../content/sound/index.js'
import Scroller from '../../Utilities/Scroller.vue'
import ImageSrcSet from '../../Media/Images/SrcSet.vue'
import globalEventStore from '../../../lib/global-event-store'

function splitIntoSections(captions, key) {
  return captions.reduce((res, cv, idx) => {
    const item = { ...cv, idx }
    if (cv[key]) res.push([item])
    else if (res.length) res[res.length - 1].push(item)
    else res[0] = [item]

    return res
  }, [])
}

const PLAYING_STATES = {
  NOT_STARTED: 'NOT_STARTED',
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  ENDED: 'ENDED'
}

export default {
  data: () => ({
    activeSection: -1,
    status: PLAYING_STATES.NOT_STARTED,
    progress: 0,
    duration: 0,
    activated: false
  }),
  computed: {
    rootMargin() {
      return this.isIntro ? '-30% 0px -45%' : '-40% 0px -45%'
    },
    currentIndex() {
      if (!this.sound.captions) return

      const { progress, captions } = this
      const nextIndex = captions.findIndex(
        (caption, idx) =>
          progress > caption.in && (captions[idx + 1] ? progress < captions[idx + 1].in : true)
      )
      return nextIndex !== undefined ? nextIndex : this.currentIndex
    },
    captions() {
      const captions = this.sound.captions
      return captions || []
    },
    sectionCaptions() {
      return splitIntoSections(this.captions, 'newLine')
    },
    sound() {
      return getSound(this.soundId)
    },
    formattedDuration() {
      if (this.status === PLAYING_STATES.NOT_STARTED) {
        return `00:${String(Number(this.duration).toFixed()).padStart(2, '0')}`
      }
      if (this.status === PLAYING_STATES.PLAYING || this.progress) {
        return `00:${String(Number(this.progress).toFixed()).padStart(2, '0')}`
      }
    },
    showInsentive() {
      return this.status === PLAYING_STATES.NOT_STARTED
    },
    showNext() {
      return (
        this.status === PLAYING_STATES.ENDED &&
        (this.activeSection === -1 || this.activeSection === this.sectionCaptions.length - 1)
      )
    }
  },
  props: {
    soundId: {
      type: String
    },
    imageId: {
      type: String
    },
    isIntro: {
      type: Boolean,
      default: false
    },
    callToAction: {
      type: String,
      default: 'Hør lyden'
    },
    extraDark: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    import('wavesurfer').then(WaveSurfer => {
      this.voiceInstance = Object.create(WaveSurfer)

      this.voiceInstance.init({
        container: this.$refs.wave,
        waveColor: '#717070',
        progressColor: '#D8D8D8',
        responsive: true,
        height: 50
      })

      globalEventStore.dispatch(this.$root.$data.id, 'sendGaEvent', {
        action: `VOICE_INIT/${this.soundId}`
      })
      this.voiceInstance.on('ready', () => {
        this.duration = this.voiceInstance.getDuration()
        globalEventStore.dispatch(this.$root.$data.id, 'sendGaEvent', {
          action: `VOICE_READY/${this.soundId}`
        })
      })
      this.voiceInstance.on('seek', () => {
        this.progress = this.voiceInstance.getCurrentTime()
        globalEventStore.dispatch(this.$root.$data.id, 'sendGaEvent', {
          action: `VOICE_SEEK/${this.soundId}/${this.voiceInstance.getCurrentTime()}`
        })
      })
      this.voiceInstance.on('play', () => {
        this.startProgressListener()
        this.status = PLAYING_STATES.PLAYING
        globalEventStore.dispatch(this.$root.$data.id, 'sendGaEvent', {
          action: `VOICE_PLAY/${this.soundId}`
        })
        if (!this.activated) {
          this.activated = true
        }
      })
      this.voiceInstance.on('pause', () => {
        if (this.state !== PLAYING_STATES.ENDED) {
          globalEventStore.dispatch(this.$root.$data.id, 'sendGaEvent', {
            action: `VOICE_PAUSE/${this.soundId}`
          })
          if (this.progress.toFixed() === this.duration.toFixed()) {
            this.status = PLAYING_STATES.ENDED
          } else {
            this.status = PLAYING_STATES.PAUSED
          }
          this.stopProgressListener()
        }
      })
      this.voiceInstance.on('finish', () => {
        globalEventStore.dispatch(this.$root.$data.id, 'sendGaEvent', {
          action: `VOICE_FINISH/${this.soundId}`
        })
        this.status = PLAYING_STATES.ENDED
        this.stopProgressListener()
        this.handleFinish()
      })

      this.voiceInstance.on('seek', () => {
        if (this.status !== PLAYING_STATES.PLAYING) this.voiceInstance.play()
      })
      this.voiceInstance.load(this.sound.src)
    })
  },
  beforeDestroy() {
    this.voiceInstance.removeListeners()
  },
  methods: {
    handleActiveChange(e) {
      this.activeSection = e
    },
    startProgressListener() {
      let lastProgress = 0
      this.progressListener = setInterval(() => {
        const newProgress = this.voiceInstance.getCurrentTime().toFixed()
        if (lastProgress !== newProgress) {
          lastProgress = newProgress

          globalEventStore.dispatch(this.$root.$data.id, 'sendGaEvent', {
            action: `VOICE_PROGRESS/${this.soundId}/${newProgress}`
          })
        }
        this.progress = Number(this.voiceInstance.getCurrentTime())
      }, 100)
    },
    stopProgressListener() {
      clearInterval(this.progressListener)
      this.progressListener = null
    },
    handleMetadataLoaded(e) {
      this.duration = Number(this.voiceInstance.audioEl.duration).toFixed()
    },
    handleProgress(time) {
      this.progress = Number(time).toFixed()
    },
    handleSeek(time) {
      if (this.activated) {
        this.voiceInstance.pause()
        this.voiceInstance.seekTo((time + 0.01) / this.duration)
      } else {
        this.voiceInstance.play()
      }
    },
    handleTogglePlay(evt) {
      evt.stopPropagation()
      if (this.status === PLAYING_STATES.PLAYING) {
        this.voiceInstance.pause()
      } else {
        this.voiceInstance.play()
      }
    },
    handleStatusChange(status) {
      // if (this.progress === this.duration) {
      //   this.status = PLAYING_STATES.ENDED
      // }
      // if (this.progress === this.duration) {
      //   this.voiceInstance.reset()
      // }
      // this.status = status === 'playing' ? PLAYING_STATES.PAUSED : PLAYING_STATES.PLAYING
    },
    handleFinish() {
      this.stauts = PLAYING_STATES.ENDED

      // const target = this.$refs.wrapper.parentElement.nextSibling

      // const targetRect = target.getBoundingClientRect()
      // const { y, height } = targetRect

      // const middle = window.innerHeight / 1.3
      // const distance = middle - y - height / 2

      // if (distance !== 0) {
      //   window.scroll({ left: 0, top: window.scrollY - distance, behavior: 'smooth' })
      // }
    }
  },
  watch: {
    currentIndex(nextValue) {
      const sections = [
        ...this.$refs.foreground.querySelectorAll('.dh-psy-voice__captions-section')
      ]
      const sectionIdx = this.sectionCaptions.findIndex(section =>
        section.find(caption => caption.idx === this.currentIndex)
      )
      if (sections[sectionIdx]) {
        const target = sections[sectionIdx].firstChild
        const targetRect = target.getBoundingClientRect()
        const { top, height } = targetRect

        const middle = window.innerHeight / 2
        const distance = middle - top - height / 2
        if (distance !== 0) {
          const offsetTop = window.scrollY || window.pageYOffset
          window.scrollTo(0, offsetTop - distance)
        }
      }
    }
  },
  components: {
    Scroller,
    ImageSrcSet
  }
}
</script>

<style lang="postcss" scoped>
  .dh-psy-voice__line-break {
  }

  .dh-psy-voice__fg {
    position: relative;
    z-index: 2;
  }

  .dh-psy-voice__sticky {
    width: 100%;
    position: fixed;
    bottom: 0;
    height: 30vh;
    height: calc(30 * var(--dh-vh));
    left: 0;
    right: 0;
    wdith: 100%;
    margin: 0;
    z-index: 1;
    & .dh-psy-voice__control {
      width: 90%;
      max-width: 450px;
      margin: auto;
    }
  }

  .dh-psy-voice {
    margin-top: -40vh;
    margin-top: calc(-40 * var(--dh-vh));

    & .dh-psy-voice__sticky {
      transition: 0.5s 0.3s opacity ease-in-out, 0.5s 0.3s transform ease-in-out;
      opacity: 0;
      transform: translateY(10px);
    }
  }

  .dh-psy-voice--in-view {
    & .dh-psy-voice__sticky {
      opacity: 1;
      transform: translateY(0);
    }
    & .dh-psy-voice__bg {
      opacity: 1;
    }
  }

  :global(.dh-no-sticky) {
    & .dh-psy-voice__bg {
      opacity: 0;
    }
    & .dh-psy-voice--in-view .dh-psy-voice__bg {
      opacity: 1;
    }

    & .dh-psy-voice__bg {
      left: 2vw;
      right: 2vw;
    }

    & .dh-psy-voice__sticky {
      position: fixed;
      left: 0;
      right: 0;

      & .dh-psy-voice__control {
        width: 90%;
        max-width: 90%;
        margin: auto;
      }
    }
  }

  .dh-psy-voice__bg {
    transition: 0.33s transform linear, 0.33s opacity linear;
    transform-origin: center;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    pointer-events: none;
    width: 100%;
    height: 100vh;
    height: calc(100 * var(--dh-vh));
    background: #111;
    z-index: 1;

    & > :global(div) {
      opacity: 0.2;
    }
  }

  :global(.dh-no-sticky) {
    & .dh-psy-voice__bg {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }

  .dh-psy-voice__control {
    position: relative;
    text-align: center;
    margin: 40px auto 20px;
    display: flex;
    justify-content: space-between;
    width: 90%;
    box-sizing: border-box;
    background: none;
    padding-top: 50px;
    transition: opacity 0.5s 0.5s ease-in-out;

    &.dh-psy-voice__control--hidden {
      opacity: 0;
      pointer-events: none;
    }

    & button {
      width: 41px;
      position: relative;
      background: #282828;
      color: #fff;
      border: none;

      & svg {
        position: absolute;
        top: 0.75em;
        left: 0.75em;
      }
    }
  }

  .dh-psy-voice__insentive {
    font-size: 0.9em;
    color: #ddd;
    margin-top: 45px;
    transition: opacity 0.5s ease-in-out;

    &.dh-psy-voice__insentive--hidden {
      opacity: 0;
    }

    & button:hover {
      text-decoration: underline;
    }
  }

  .dh-psy-voice__scroll-next {
    color: #fff;
    line-height: 50px;
    margin-top: -50px;
    transition: opacity 0.5s 0.5s ease-in-out;
    animation-name: dh-psy-scroll-next;
    animation-duration: 1s;
    animation-delay: 2s;
    animation-fill-mode: forwards;
    animation-direction: alternate;
    animation-iteration-count: infinite;

    @media (min-height: 800px) {
      margin-top: -20px;
    }

    &.dh-psy-voice__scroll-next--hidden {
      opacity: 0;
      pointer-events: none;
      animation-play-state: paused;
    }

    & button.nrk-unset {
      &:hover {
        text-decoration: underline;
      }
    }
  }

  @keyframes dh-psy-scroll-next {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(10px);
    }
  }

  .dh-psy-voice {
    position: relative;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: -80vh;
    margin-bottom: calc(-80 * var(--dh-vh));

    text-align: center;
    width: 100%;
    max-width: 100%;
  }

  .dh-psy-voice__wave {
    width: calc(100% - (82px + 10px));
    box-sizing: border-box;
  }
  .dh-psy-voice__duration {
    width: 41px;
    line-height: 50px;
    font-size: 0.9em;
    text-align: right;
    z-index: 1;
    color: #aaa;
  }

  .dh-psy-voice__captions-section {
    height: 80vh;
    height: calc(80 * var(--dh-vh));
    pointer-events: none;
    text-align: left;
    opacity: 0;
    transition: 0.5s opacity ease-in-out;
    font-size: 1.2em;
    max-width: 95%;
    width: 80%;

    max-width: 650px;
    margin: auto;

    @media (min-width: 800px) {
      height: 60vh;
      height: calc(60 * var(--dh-vh));

      font-size: 1.5em;
      max-width: 590px;
    }

    &:first-of-type {
      margin-top: calc(22vh - 3.6em);
      margin-top: calc(22 * var(--dh-vh) - 3.6em);
    }

    &:last-of-type {
      height: 60vh;
      height: calc(60 * var(--dh-vh));
    }

    &.dh-psy-voice__captions-section--active {
      opacity: 1;
    }

    &.dh-psy-voice__captions-section--passed {
      opacity: 0.2;
    }
  }

  :global(.dh-psy-voice__caption-item) {
    display: inline;
    line-height: 1;
    line-height: 1.6;
    font-family: NRK Etica Slab;
    color: #fff;
    text-shadow: 0 1px 2px rgba(32, 31, 31, 0.158), 0 1px 10px #44414161,
      0 1px 2px rgba(32, 31, 31, 0.158), 0 1px 10px #44414161, 0 1px 2px rgba(32, 31, 31, 0.158),
      0 1px 10px #44414161, 0 1px 2px rgba(32, 31, 31, 0.158), 0 1px 10px #44414161;
    transition: opacity 0.4s cubic-bezier(0.4, 0, 1, 1);
    pointer-events: auto;
  }

  .dh-psy-voice__captions {
    margin-bottom: calc(50vh);
    margin-bottom: calc(50 * var(--dh-vh));
  }

  .dh-psy-voice__captions--activated {
    & .dh-psy-voice__caption-item {
      opacity: 0.1;
    }

    & .dh-psy-voice__caption-item--passed {
      opacity: 1;
    }

    & .dh-psy-voice__caption-item--active {
      opacity: 1;
    }
  }

  :global(.dh-psy-voice__caption-item--passed) {
    opacity: 0.8;
  }

  :global(.dh-psy-voice__caption-item--active) {
    opacity: 1;
  }

  .dh-psy-voice--not-intro {
    & .dh-psy-voice__bg {
      background: #000;
      position: sticky;
      height: calc(100vh - 4vw);
      height: calc(100 * var(--dh-vh) - 4vw);

      width: calc(100% - 4vw);
      top: 2vw;

      @media (min-width: 800px) {
        top: 5vh;
        top: calc(5 * var(--dh-vh));
        height: calc(90vh);
        height: calc(90 * var(--dh-vh));

        width: calc(100% - 10vw);
      }
    }
    margin-top: 10vh;
    margin-top: calc(10 * var(--dh-vh));

    margin-bottom: 0;

    & .dh-psy-voice__captions-section:last-of-type {
      height: auto;
    }

    & .dh-psy-voice__sticky {
      position: sticky;
      top: 70vh;
      top: calc(70 * var(--dh-vh));
    }

    & .dh-psy-voice__captions {
      margin-top: -70vh;
      margin-top: calc(-70 * var(--dh-vh));
    }

    & .dh-psy-voice__caption-item {
      text-shadow: none;
    }
  }

  .dh-psy-voice--in-view .dh-psy-voice__bg {
    opacity: 1;
  }

  :global(.dh-no-sticky) {
    & .dh-psy-voice--not-intro {
      margin: 50vh 0;
      margin: calc(50 * var(--dh-vh)) 0;

      & .dh-psy-voice__captions {
        margin: calc(40vh - 10vw) 0;
        margin: calc(40 * var(--dh-vh) - 10vw) 0;
      }

      & .dh-psy-voice__bg {
        left: 2vw;
        transition: 0.5s opacity ease-in-out;

        @media (min-width: 800px) {
          left: 5vw;
        }
      }
    }
  }
</style>
